import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapPin, Navigation, Loader2, X, Building2, Home, CheckCircle } from 'lucide-react';

/**
 * Smart Location Input for Trainer Profile
 * 
 * Two ways to set location:
 * 1. Use GPS location (My Location button)
 * 2. Type address and select from suggestions
 */
const SmartLocationInput = ({
  onPlaceSelect,
  defaultValue = '',
  placeholder = 'Enter your address or city...',
  showCurrentLocation = true,
}) => {
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const autocompleteServiceRef = useRef(null);
  const placesServiceRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const debounceTimerRef = useRef(null);

  const [inputValue, setInputValue] = useState(defaultValue);
  const [predictions, setPredictions] = useState({ locations: [], addresses: [] });
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);

  // Update input value when defaultValue changes
  useEffect(() => {
    if (defaultValue && !inputValue) {
      setInputValue(defaultValue);
    }
  }, [defaultValue]);

  // Load Google Maps Script
  useEffect(() => {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!apiKey) {
      console.warn('Google Maps API key not found');
      return;
    }

    if (window.google?.maps?.places) {
      setIsScriptLoaded(true);
      initializeServices();
      return;
    }

    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      const checkLoaded = setInterval(() => {
        if (window.google?.maps?.places) {
          setIsScriptLoaded(true);
          initializeServices();
          clearInterval(checkLoaded);
        }
      }, 100);
      setTimeout(() => clearInterval(checkLoaded), 10000);
      return () => clearInterval(checkLoaded);
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.onload = () => {
      setIsScriptLoaded(true);
      initializeServices();
    };
    document.head.appendChild(script);
  }, []);

  // Initialize Google services
  const initializeServices = () => {
    if (!window.google?.maps?.places) return;
    
    autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
    
    // Create PlacesService with a hidden div
    const serviceDiv = document.createElement('div');
    serviceDiv.style.display = 'none';
    document.body.appendChild(serviceDiv);
    placesServiceRef.current = new window.google.maps.places.PlacesService(serviceDiv);
    
    // Create session token for billing optimization
    sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
  };

  // Re-initialize services when script loads
  useEffect(() => {
    if (isScriptLoaded) {
      initializeServices();
    }
  }, [isScriptLoaded]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch predictions
  const fetchPredictions = useCallback((query) => {
    if (!autocompleteServiceRef.current || query.length < 2) {
      setPredictions({ locations: [], addresses: [] });
      return;
    }

    setIsLoading(true);

    const baseRequest = {
      input: query,
      componentRestrictions: { country: 'ca' },
      sessionToken: sessionTokenRef.current
    };

    // Fetch locations (cities, regions)
    const locationRequest = { ...baseRequest, types: ['(regions)'] };
    
    // Fetch addresses
    const addressRequest = { ...baseRequest, types: ['address'] };

    Promise.all([
      new Promise((resolve) => {
        autocompleteServiceRef.current.getPlacePredictions(locationRequest, (results, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && results) {
            resolve(results.slice(0, 4));
          } else {
            resolve([]);
          }
        });
      }),
      new Promise((resolve) => {
        autocompleteServiceRef.current.getPlacePredictions(addressRequest, (results, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && results) {
            resolve(results.slice(0, 3));
          } else {
            resolve([]);
          }
        });
      })
    ]).then(([locations, addresses]) => {
      setPredictions({ locations, addresses });
      setIsLoading(false);
      setHighlightedIndex(-1);
    }).catch((error) => {
      console.error('Error fetching predictions:', error);
      setIsLoading(false);
    });
  }, []);

  // Debounced input handler
  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    setShowDropdown(true);
    setSelectedLocation(null); // Clear selection when typing

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      fetchPredictions(value);
    }, 300);
  };

  // Select a prediction from dropdown
  const handleSelectPrediction = async (prediction) => {
    if (!placesServiceRef.current) {
      console.error('PlacesService not initialized');
      // Try to re-initialize
      initializeServices();
      if (!placesServiceRef.current) {
        alert('Google Maps service not available. Please refresh the page.');
        return;
      }
    }

    setIsLoading(true);
    setShowDropdown(false);
    setInputValue(prediction.description);

    try {
      placesServiceRef.current.getDetails(
        {
          placeId: prediction.place_id,
          fields: ['geometry', 'formatted_address', 'address_components', 'name', 'types', 'place_id'],
          sessionToken: sessionTokenRef.current
        },
        (place, status) => {
          setIsLoading(false);
          
          // Create new session token after selection (billing optimization)
          sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
          
          if (status === window.google.maps.places.PlacesServiceStatus.OK && place?.geometry?.location) {
            const locationData = parsePlace(place, prediction);
            setSelectedLocation(locationData);
            onPlaceSelect?.(locationData);
            console.log('Location selected from dropdown:', locationData);
          } else {
            console.error('Error getting place details:', status);
            alert('Could not get location details. Please try again.');
          }
        }
      );
    } catch (error) {
      console.error('Error in getDetails:', error);
      setIsLoading(false);
      alert('Error getting location details. Please try again.');
    }
  };

  // Parse place details into location data
  const parsePlace = (place, prediction) => {
    const components = place.address_components || [];
    
    const getComponent = (types) => {
      const component = components.find(c => types.some(type => c.types.includes(type)));
      return component?.long_name || '';
    };

    const getShortComponent = (types) => {
      const component = components.find(c => types.some(type => c.types.includes(type)));
      return component?.short_name || '';
    };

    return {
      formatted_address: place.formatted_address,
      place_id: prediction?.place_id || place.place_id,
      street: getComponent(['route']),
      street_number: getComponent(['street_number']),
      city: getComponent(['locality', 'sublocality', 'administrative_area_level_3']),
      province: getShortComponent(['administrative_area_level_1']),
      country: getComponent(['country']) || 'Canada',
      postal_code: getComponent(['postal_code']),
      coordinates: [
        place.geometry.location.lng(),
        place.geometry.location.lat()
      ]
    };
  };

  // Keyboard navigation
  const handleKeyDown = (e) => {
    const allPredictions = [...predictions.locations, ...predictions.addresses];
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => prev < allPredictions.length - 1 ? prev + 1 : prev);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Enter' && highlightedIndex >= 0) {
      e.preventDefault();
      handleSelectPrediction(allPredictions[highlightedIndex]);
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  // Get current GPS location
  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }

    setGettingLocation(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        
        try {
          const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
          const response = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${apiKey}`
          );
          const data = await response.json();
          
          if (data.results?.[0]) {
            const result = data.results[0];
            const components = result.address_components || [];
            
            const getComponent = (types) => {
              const component = components.find(c => types.some(type => c.types.includes(type)));
              return component?.long_name || '';
            };

            const getShortComponent = (types) => {
              const component = components.find(c => types.some(type => c.types.includes(type)));
              return component?.short_name || '';
            };

            const locationData = {
              formatted_address: result.formatted_address,
              place_id: result.place_id,
              street: getComponent(['route']),
              street_number: getComponent(['street_number']),
              city: getComponent(['locality', 'sublocality']),
              province: getShortComponent(['administrative_area_level_1']),
              country: getComponent(['country']) || 'Canada',
              postal_code: getComponent(['postal_code']),
              coordinates: [longitude, latitude]
            };

            setInputValue(result.formatted_address);
            setSelectedLocation(locationData);
            setShowDropdown(false);
            onPlaceSelect?.(locationData);
            console.log('Location selected from GPS:', locationData);
          }
        } catch (error) {
          console.error('Geocoding error:', error);
          alert('Could not get your location details. Please try typing your address instead.');
        } finally {
          setGettingLocation(false);
        }
      },
      (error) => {
        setGettingLocation(false);
        if (error.code === error.PERMISSION_DENIED) {
          alert('Location access denied. Please enable location in your browser settings, or type your address manually.');
        } else {
          alert('Could not get your location. Please try typing your address instead.');
        }
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // Clear input
  const handleClear = () => {
    setInputValue('');
    setPredictions({ locations: [], addresses: [] });
    setShowDropdown(false);
    setSelectedLocation(null);
    onPlaceSelect?.(null);
    inputRef.current?.focus();
  };

  const allPredictions = [...predictions.locations, ...predictions.addresses];
  const hasResults = allPredictions.length > 0;

  return (
    <div className="space-y-2">
      {/* Helper text */}
      <p className="text-xs text-gray-500 flex items-center gap-2">
        <span className="inline-flex items-center gap-1">
          <Navigation className="w-3 h-3 text-red-500" /> Use GPS
        </span>
        <span className="text-gray-300">|</span>
        <span className="inline-flex items-center gap-1">
          <MapPin className="w-3 h-3 text-blue-500" /> Type address
        </span>
      </p>

      {/* Input Field */}
      <div className="relative">
        <div className="relative">
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-red-500 z-10" />
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (inputValue.length >= 2) {
                setShowDropdown(true);
                fetchPredictions(inputValue);
              }
            }}
            placeholder={placeholder}
            disabled={!isScriptLoaded}
            className="w-full pl-10 pr-24 py-2.5 text-sm border rounded-lg 
              bg-white dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600
              focus:ring-2 focus:ring-red-500 focus:border-red-500 focus:outline-none
              placeholder:text-gray-400 transition-all disabled:opacity-50"
          />
          
          {/* Right side buttons */}
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
            {isLoading && <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />}
            {inputValue && !isLoading && (
              <button
                type="button"
                onClick={handleClear}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                title="Clear"
              >
                <X className="w-4 h-4 text-gray-400" />
              </button>
            )}
            {showCurrentLocation && (
              <button
                type="button"
                onClick={getCurrentLocation}
                disabled={gettingLocation}
                className="p-1.5 bg-red-50 dark:bg-red-900/30 hover:bg-red-100 dark:hover:bg-red-900/50 rounded text-red-500 transition-colors"
                title="Use my current location (GPS)"
              >
                {gettingLocation ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Navigation className="w-4 h-4" />
                )}
              </button>
            )}
          </div>
        </div>

        {/* Predictions Dropdown */}
        {showDropdown && (inputValue.length >= 2 || hasResults) && (
          <div 
            ref={dropdownRef}
            className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-xl 
              border border-gray-200 dark:border-gray-700 overflow-hidden"
          >
            {isLoading && !hasResults ? (
              <div className="p-3 text-center text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin mx-auto mb-1" />
                <span className="text-xs">Searching...</span>
              </div>
            ) : hasResults ? (
              <div className="max-h-64 overflow-y-auto">
                {/* Locations Group */}
                {predictions.locations.length > 0 && (
                  <div>
                    <div className="px-3 py-1.5 bg-gray-50 dark:bg-gray-700/50 text-[10px] font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
                      <Building2 className="w-3 h-3" />
                      Locations
                    </div>
                    {predictions.locations.map((prediction, index) => (
                      <button
                        key={prediction.place_id}
                        type="button"
                        onClick={() => handleSelectPrediction(prediction)}
                        className={`w-full px-3 py-2 text-left hover:bg-red-50 dark:hover:bg-red-900/20 
                          transition-colors flex items-start gap-2 border-b border-gray-100 dark:border-gray-700/50
                          ${highlightedIndex === index ? 'bg-red-50 dark:bg-red-900/20' : ''}`}
                      >
                        <div className="mt-0.5 p-1 bg-blue-100 dark:bg-blue-900/30 rounded">
                          <Building2 className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {prediction.structured_formatting?.main_text || prediction.description}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {prediction.structured_formatting?.secondary_text || ''}
                          </p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {/* Addresses Group */}
                {predictions.addresses.length > 0 && (
                  <div>
                    <div className="px-3 py-1.5 bg-gray-50 dark:bg-gray-700/50 text-[10px] font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
                      <Home className="w-3 h-3" />
                      Addresses
                    </div>
                    {predictions.addresses.map((prediction, index) => {
                      const adjustedIndex = predictions.locations.length + index;
                      return (
                        <button
                          key={prediction.place_id}
                          type="button"
                          onClick={() => handleSelectPrediction(prediction)}
                          className={`w-full px-3 py-2 text-left hover:bg-red-50 dark:hover:bg-red-900/20 
                            transition-colors flex items-start gap-2 border-b border-gray-100 dark:border-gray-700/50 last:border-b-0
                            ${highlightedIndex === adjustedIndex ? 'bg-red-50 dark:bg-red-900/20' : ''}`}
                        >
                          <div className="mt-0.5 p-1 bg-green-100 dark:bg-green-900/30 rounded">
                            <Home className="w-3 h-3 text-green-600 dark:text-green-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {prediction.structured_formatting?.main_text || prediction.description}
                            </p>
                            <p className="text-xs text-gray-500 truncate">
                              {prediction.structured_formatting?.secondary_text || ''}
                            </p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : inputValue.length >= 2 ? (
              <div className="p-3 text-center text-gray-500 text-xs">
                No results found for "{inputValue}"
              </div>
            ) : null}
          </div>
        )}
      </div>

      {/* Selected location indicator */}
      {selectedLocation && (
        <div className="flex items-center gap-2 text-xs text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-2 py-1.5 rounded">
          <CheckCircle className="w-3.5 h-3.5" />
          <span>Location set: {selectedLocation.city || selectedLocation.formatted_address?.split(',')[0]}{selectedLocation.province ? `, ${selectedLocation.province}` : ''}</span>
        </div>
      )}
    </div>
  );
};

export default SmartLocationInput;
